import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Sidebar,
    SidebarHeader,
    SidebarContent,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarProvider,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton,
} from '@/components/ui/sidebar';
import { useAppSelector } from '@/store';
import {
    selectCurrentUser,
    selectHasStore,
    selectIsImpersonating,
    selectIsSuperAdmin,
} from '@/store/slices/authSlice';
// ─── [RELEASE 3: Super Admin] ────────────────────────────────────
// ─── [/RELEASE 3] ────────────────────────────────────────────────
import InitialAvatar from '../common/InitialAvatar';
import {
    Star,
    Settings,
    HelpCircle,
    ChevronDown,
    ChevronUp,
    LogOut,
    User,
    Shield, // [RELEASE 3: Super Admin]
    Users,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { useFeaturePermission } from '@/hooks/useFeaturePermission';
import { PERMISSIONS } from '@/features/auth/constants';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { useAnalyticsClick } from '../common/AnalyticsButton';
import { AgenticModeToggle } from '@/features/agent';
// ─── [RELEASE 1: Multi-Merchant Workspace Switching — DONE] ──────
import WorkspaceSwitcher from './WorkspaceSwitcher';
// ─── [/RELEASE 1] ────────────────────────────────────────────────
import StoreSwitcher from './StoreSwitcher';
import { useStoreAccessDenied } from '@/hooks/useStoreAccessDenied';
import { hasImpersonationRedirectHint } from '@/utils/impersonationHint';

interface SidebarNavProps {
    agenticMode?: boolean
    onAgenticToggle?: (next: boolean) => void
    agenticDisabled?: boolean
}

const SidebarNav: React.FC<SidebarNavProps> = ({
    agenticMode = false,
    onAgenticToggle,
    agenticDisabled = false,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useAppSelector(selectCurrentUser);
    // ─── [RELEASE 3: Super Admin] ──────────────────────────────────
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
    const isImpersonating = useAppSelector(selectIsImpersonating);
    /** Merchant JWT omits super_admin from roles; still show Admin Panel while impersonating. */
    const showAdminPanel =
        isSuperAdmin ||
        isImpersonating ||
        hasImpersonationRedirectHint();
    // ─── [/RELEASE 3] ───────────────────────────────────────────────
    const hasStore = useAppSelector(selectHasStore);
    const { logout } = useAuth();
    const { trackClick } = useAnalyticsClick();

    // Global listener for 403 store-access-denied events
    useStoreAccessDenied();

    // Permission checks for sidebar items
    const canManageTeam = usePermission(PERMISSIONS.TEAM_MANAGE);
    const { isFeatureEnabled: isTeamModuleEnabled } = useFeaturePermission({
        permission: PERMISSIONS.TEAM_MANAGE,
        featureKey: 'team_module',
    });
    const canViewBilling = usePermission(PERMISSIONS.BILLING_READ);
    const canViewIntegrations = usePermission(PERMISSIONS.INTEGRATIONS_READ);
    const canViewAnalytics = usePermission(PERMISSIONS.ANALYTICS_READ);
    const { hasAccess: _canAccessNps } = useFeaturePermission({
        permission: PERMISSIONS.ANALYTICS_READ,
        featureKey: 'nps_tab',
    });
    const canAccessNps = _canAccessNps || import.meta.env.VITE_NPS_DEMO_MODE === 'true';

    const [surveyOpen, setSurveyOpen] = React.useState(true);
    const [settingsOpen, setSettingsOpen] = React.useState(false);

    useEffect(() => {
        // Auto-open settings submenu if on a settings page
        if (location.pathname.startsWith('/settings')) {
            setSettingsOpen(true);
        }
    }, [location.pathname]);

    const menuItems = [
        {
            id: 'surveys',
            icon: Star,
            label: 'Surveys',
            path: '/',
            disabled: false,
            hasSubmenu: true,
            submenu: [{ label: 'Templates', path: '/surveys/templates' }],
        },
    ];

    const settingsSubmenu = [
        {
            id: 'integrations-settings',
            label: 'Integrations',
            path: '/settings/integrations',
            visible: canViewIntegrations,
        },
        {
            id: 'team',
            label: 'Team',
            path: '/settings/team',
            visible: canManageTeam && isTeamModuleEnabled,
        },
        {
            id: 'billing',
            label: 'Billing',
            path: '/settings/billing',
            visible: canViewBilling && hasStore,
        },
        {
            id: 'mcp',
            label: 'Claude MCP',
            path: '/settings/mcp',
            visible: true,
        },
        {
            id: 'profile',
            label: 'Profile',
            path: '/settings/profile',
            visible: true,
        },
    ];

    const moreItems = [
        {
            id: 'help',
            icon: HelpCircle,
            label: 'Help Centre',
            path: 'https://compra.gitbook.io/docs.compra/',
            disabled: false,
            external: true,
        },
        {
            id: 'settings',
            icon: Settings,
            label: 'Settings',
            path: '/settings',
            disabled: false,
            hasSubmenu: true,
        },
    ];

    const handleMenuClick = (item: any) => {
        if (item.disabled) return;

        if (item.id === 'help') {
            trackClick('help_center');
        } else if (item.id === 'settings') {
            trackClick('navigation', { destination: 'settings' });
        } else if (item.id === 'surveys') {
            trackClick('navigation', { destination: 'surveys' });
        }

        if (item.external) {
            window.open(item.path, '_blank', 'noopener,noreferrer');
            return;
        }

        if (item.id === 'surveys') {
            if (location.pathname === '/') {
                setSurveyOpen((prev) => !prev);
            } else {
                navigate('/');
                setSurveyOpen(true);
            }
        } else if (item.id === 'settings') {
            if (location.pathname.startsWith('/settings')) {
                setSettingsOpen((prev) => !prev);
            } else {
                navigate('/settings');
                setSettingsOpen(true);
            }
        } else {
            navigate(item.path);
        }
    };

    const userDisplayName = user?.email?.split('@')[0] || 'User';
    const userEmail = user?.email || '';

    return (
        <SidebarProvider>
            <Sidebar className="h-screen w-full bg-[#FAFAFA] border-r border-gray-200 flex flex-col justify-between shadow-sm">
                {/* Header */}
                <SidebarHeader className="flex items-start justify-start h-16">
                    <img
                        src="/compra-text.svg"
                        alt="Compra Logo"
                        className="w-40 h-auto transition-opacity duration-200 hover:opacity-80"
                        loading="lazy"
                    />
                </SidebarHeader>

                {/* Content */}
                <SidebarContent className="flex-1 px-3 py-4 overflow-y-auto space-y-2">
                    {onAgenticToggle && (
                        <div className="px-1 pb-2">
                            <AgenticModeToggle
                                checked={agenticMode}
                                onCheckedChange={onAgenticToggle}
                                disabled={agenticDisabled}
                                className="w-full justify-start"
                            />
                        </div>
                    )}
                    <SidebarMenu className="space-y-1">
                        {/* Main Menu Items */}
                        <SidebarGroup className="space-y-1 p-0">
                            <SidebarGroupLabel>Create</SidebarGroupLabel>
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;

                                return (
                                    <div key={item.id} className="space-y-1">
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                onClick={() => handleMenuClick(item)}
                                                disabled={item.disabled}
                                                className={`
                          w-full h-10 flex items-center justify-between px-3 py-2.5 rounded-lg
                          transition-all duration-200 ease-in-out group
                          ${isActive
                                                        ? 'bg-[#EBEBEB] text-[#303030] shadow-sm'
                                                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                                    }
                          ${item.disabled
                                                        ? 'opacity-50 cursor-not-allowed'
                                                        : 'cursor-pointer hover:shadow-sm'
                                                    }
                        `}
                                            >
                                            <span className="flex items-center gap-3">
                                                <Icon
                                                    fill={isActive ? '#303030' : 'white'}
                                                    className="w-4 h-4 transition-all duration-200"
                                                />
                                                <span className="font-medium text-sm">
                                                    {item.label}
                                                </span>
                                            </span>
                                            {item.hasSubmenu && (
                                                <div
                                                    className={`
                              transition-transform duration-200 ease-in-out
                              ${surveyOpen ? 'rotate-180' : 'rotate-0'}
                            `}
                                                >
                                                    <ChevronDown
                                                        className={`
                                w-4 h-4 transition-colors duration-200
                                ${isActive
                                                                ? 'text-orange-600'
                                                                : 'text-gray-400 group-hover:text-gray-600'
                                                            }
                              `}
                                                    />
                                                </div>
                                            )}
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>

                                        {/* Submenu */ }
                                {
                                    item.hasSubmenu && (
                                        <div
                                            className={`
                          overflow-hidden transition-all duration-300 ease-in-out
                          ${surveyOpen
                                                    ? 'max-h-32 opacity-100'
                                                    : 'max-h-0 opacity-0'
                                                }
                        `}
                                        >
                                            <SidebarMenuSub>
                                                {item.submenu?.map((subItem) => (
                                                    <SidebarMenuSubItem
                                                        key={subItem.path}
                                                        className="cursor-pointer"
                                                    >
                                                        <SidebarMenuSubButton
                                                            onClick={() => {
                                                                trackClick('navigation', {
                                                                    destination: 'templates',
                                                                });
                                                                navigate(subItem.path);
                                                            }}
                                                            isActive={
                                                                location.pathname === subItem.path
                                                            }
                                                            className="h-10"
                                                        >
                                                            {subItem.label}
                                                        </SidebarMenuSubButton>
                                                    </SidebarMenuSubItem>
                                                ))}
                                            </SidebarMenuSub>
                                        </div>
                                    )
                                }
                                    </div>
                        );
                            })}
                    </SidebarGroup>

                    {/* Insights */}
                    {canViewAnalytics && canAccessNps && (
                        <SidebarGroup className="space-y-1 p-0 mt-1">
                            <SidebarGroupLabel>Insights</SidebarGroupLabel>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    onClick={() => navigate('/analytics/nps-by-customer')}
                                    className={`
                                        w-full h-10 flex items-center justify-start px-3 py-2.5 rounded-lg
                                        transition-all duration-200 ease-in-out group
                                        ${location.pathname === '/analytics/nps-by-customer'
                                            ? 'bg-[#EBEBEB] text-[#303030] shadow-sm'
                                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                        }
                                        cursor-pointer hover:shadow-sm
                                    `}
                                >
                                    <span className="flex items-center gap-3">
                                        <Users
                                            className={`w-4 h-4 transition-colors duration-200 ${location.pathname === '/analytics/nps-by-customer'
                                                ? 'text-orange-600'
                                                : 'text-gray-500 group-hover:text-gray-700'
                                            }`}
                                        />
                                        <span className="font-medium text-sm">NPS by Customer</span>
                                    </span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarGroup>
                    )}

                    {/* ─── [RELEASE 3: Super Admin Panel] ─── */}
                    {showAdminPanel && (
                        <SidebarGroup className="p-0 mt-1">
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    onClick={() => navigate('/admin')}
                                    className={`
                                            w-full h-10 flex items-center justify-between px-3 py-2.5 rounded-lg
                                            transition-all duration-200 ease-in-out group cursor-pointer hover:shadow-sm
                                            ${location.pathname.startsWith('/admin')
                                            ? 'bg-[#EBEBEB] text-[#303030] shadow-sm'
                                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                        }
                                        `}
                                >
                                    <span className="flex items-center gap-3">
                                        <Shield
                                            className={`w-4 h-4 transition-colors duration-200 ${location.pathname.startsWith('/admin')
                                                ? 'text-orange-600'
                                                : 'text-gray-500 group-hover:text-gray-700'
                                                }`}
                                        />
                                        <span className="font-medium text-sm">Admin Panel</span>
                                    </span>
                                    <span className="text-[10px] font-[600] px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                                        SA
                                    </span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarGroup>
                    )}
                    {/* ─── [/RELEASE 3] ─── */}

                    {/* More Section */}
                    <div className="space-y-0">
                        <SidebarGroupLabel>More</SidebarGroupLabel>

                        <SidebarGroup className="space-y-1 p-0">
                            {moreItems.map((item) => {
                                const Icon = item.icon;
                                const isActive =
                                    item.id === 'settings'
                                        ? location.pathname.startsWith(item.path)
                                        : location.pathname === item.path;

                                return (
                                    <div key={item.id} className="space-y-1">
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                onClick={() =>
                                                    handleMenuClick(item)
                                                }
                                                disabled={item.disabled}
                                                className={`
                                                    w-full h-10 flex items-center justify-start px-3 py-2.5 rounded-lg
                                                    transition-all duration-200 ease-in-out group
                                                    ${isActive
                                                        ? 'bg-[#EBEBEB] text-[#303030] shadow-sm'
                                                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                                    }
                                                    ${item.disabled
                                                        ? 'opacity-50 cursor-not-allowed'
                                                        : 'cursor-pointer hover:shadow-sm'
                                                    }
                                                `}
                                            >
                                                <span className="flex items-center gap-3">
                                                    <Icon
                                                        className={`
                                                            w-4 h-4 transition-colors duration-200
                                                            ${isActive
                                                                ? 'text-orange-600'
                                                                : 'text-gray-500 group-hover:text-gray-700'
                                                            }
                                                        `}
                                                    />
                                                    <span className="font-medium text-sm">
                                                        {item.label}
                                                    </span>
                                                </span>
                                                {item.hasSubmenu && (
                                                    <ChevronDown
                                                        className={`
                                                            w-4 h-4 transition-transform duration-200
                                                            ${settingsOpen ? 'rotate-180' : 'rotate-0'}
                                                        `}
                                                    />
                                                )}
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>

                                        {/* Settings Submenu */}
                                        {item.hasSubmenu && (
                                            <div
                                                className={`
                                                    overflow-hidden transition-all duration-300 ease-in-out
                                                    ${settingsOpen
                                                        ? 'max-h-48 opacity-100'
                                                        : 'max-h-0 opacity-0'
                                                    }
                                                `}
                                            >
                                                <SidebarMenuSub>
                                                    {settingsSubmenu
                                                        .filter((sub) => sub.visible)
                                                        .map((subItem) => (
                                                            <SidebarMenuSubItem
                                                                key={subItem.path}
                                                                className="cursor-pointer"
                                                            >
                                                                <SidebarMenuSubButton
                                                                    onClick={() => navigate(subItem.path)}
                                                                    isActive={
                                                                        location.pathname === subItem.path
                                                                    }
                                                                    className="h-10"
                                                                >
                                                                    {subItem.label}
                                                                </SidebarMenuSubButton>
                                                            </SidebarMenuSubItem>
                                                        ))}
                                                </SidebarMenuSub>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </SidebarGroup>
                    </div>
                </SidebarMenu>
            </SidebarContent>

            {/* Footer */}
            <SidebarFooter className="border-t border-gray-200 bg-gray-50/50">
                {/* ─── [RELEASE 1: Multi-Merchant Workspace Switching — DONE] ─── */}
                <WorkspaceSwitcher />
                {/* ─── [/RELEASE 1] ─── */}

                {/* Store Switcher (multi-store within same merchant) */}
                <StoreSwitcher />

                {/* User Profile */}
                <div className="px-4 py-3">
                    <Popover>
                        <PopoverTrigger asChild>
                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white hover:shadow-sm transition-all duration-200 cursor-pointer group">
                                <InitialAvatar
                                    name={userDisplayName}
                                    size={36}
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-gray-900 truncate">
                                        {userDisplayName}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {userEmail}
                                    </p>
                                </div>
                                <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
                            </div>
                        </PopoverTrigger>
                        <PopoverContent
                            className="w-56 p-2"
                            align="start"
                            side="top"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            <div className="flex flex-col gap-1">
                                <SidebarMenuButton
                                    autoFocus={false}
                                    onFocus={(e) => e.preventDefault()}
                                    onClick={() => navigate('/settings/profile')}
                                    className="w-full h-10 flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200"
                                >
                                    <User className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium text-sm">Profile</span>
                                </SidebarMenuButton>
                                <SidebarMenuButton
                                    autoFocus={false}
                                    onFocus={(e) => e.preventDefault()}
                                    onClick={logout}
                                    className="w-full h-10 flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200"
                                >
                                    <LogOut className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium text-sm">Logout</span>
                                </SidebarMenuButton>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </SidebarFooter>
        </Sidebar>
        </SidebarProvider>
    );
};

export default SidebarNav;
